import camelcaseKeys from 'camelcase-keys'
import decamelizeKeys from 'decamelize-keys'
import { create } from 'zustand'
import { supabase } from '../supabaseClient'
import { TablesUpdate } from '../types/supabase'
import { getUserId } from '../lib/workos'
import { isAllowedAvatarFile, isSvgAvatarFile } from '../utilities/avatar'
import useAuth from './useAuth'
import useOrganizations from './useOrganizations'

export interface ProfileState {
  loading: boolean
  fullName: string | null
  username: string | null
  website: string | null
  avatarUrl: string | null
  avatarImage: string | null
  set: (state: Partial<ProfileState>) => void
  get: () => Promise<void>
  save: (updates: Partial<ProfileState>) => Promise<void>
  downloadAvatar: () => Promise<void>
  subscribe: () => () => void
  reset: () => void
}

const state = {
  loading: true,
  fullName: null,
  username: null,
  website: null,
  avatarUrl: null,
  avatarImage: null,
}

const useProfile = create<ProfileState>()((set, get) => ({
  ...state,
  set,
  get: async () => {
    set({ loading: true })

    // RLS policy filters to current user's profile via request_user_id()
    const { data, error } = await supabase
      .from('profiles')
      .select('full_name, website, avatar_url, username')
      .single()

    if (error) console.warn(error)
    else set(camelcaseKeys(data))

    console.log('get profile', data)

    set({ loading: false })
    get().downloadAvatar()
  },
  save: async updates => {
    set({ loading: true })

    // Profile row is created by handle_new_user() trigger when auth user is created.
    // RLS policy filters to current user's profile via request_user_id()
    const userId = getUserId()
    if (!userId) {
      alert('Profile save error: not authenticated')
      set({ loading: false })
      return
    }

    // Only the fields being saved. profiles.username is NOT NULL and generated by
    // a trigger at signup, and re-sending it here used to fall back to '' — which
    // would blank a real username whenever the store hadn't loaded one yet.
    const { error } = await supabase.from('profiles').update({
      ...decamelizeKeys(updates),
      updated_at: new Date().toISOString(),
    } as TablesUpdate<'profiles'>).eq('id', userId)

    if (error) {
      alert('Profile save error: ' + error.message)
    } else if ('fullName' in updates) {
      // An unnamed personal org's label derives from this very name (orgLabel, via
      // get_org_owners) — so the moment one is saved, every org label the app is
      // holding is stale. Most visible on the profile itself: the org strip sits
      // directly above the field, still showing the email the empty-name helper text
      // just quoted. Refetching is what picks up the new owner identity; router 0091
      // dropped the sync_personal_org_name trigger that used to do it in the DB.
      // useOrganizations doesn't import this store, so the static import is not even
      // a cycle.
      void useOrganizations.getState().get()
    }
    set({ loading: false })
  },
  downloadAvatar: async () => {
    const { avatarUrl } = get()
    const { user } = useAuth.getState()

    if (!avatarUrl) {
      const foundUrl = user?.user_metadata?.avatar_url || user?.user_metadata?.picture
      if (foundUrl) set({ avatarUrl: foundUrl })
      return
    }

    if (avatarUrl.startsWith('http')) {
      set({ avatarImage: avatarUrl })
      return
    }

    const { data, error } = await supabase.storage.from('avatars').download(get().avatarUrl || '')
    if (error) console.warn('Avatar download failed:', error.message)
    else if (!isAllowedAvatarFile(avatarUrl, data.type)) {
      if (isSvgAvatarFile(avatarUrl, data.type)) {
        console.warn('Blocked SVG avatar content:', { avatarUrl, mimeType: data.type })
      }
      set({ avatarImage: null })
    } else set({ avatarImage: URL.createObjectURL(data) })
  },
  subscribe: () => {
    // RLS policy filters to current user's profile via request_user_id()
    const channel = supabase
      .channel('profiles-all')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'profiles' },
        payload => {
          console.log('profile subscription update', payload)
          set(camelcaseKeys(payload.new))
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  },
  reset: () => set({ ...state }),
}))

export default useProfile
